import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, X, MessageSquare, ArrowRight, CheckCircle2, Circle, Edit3, Gavel } from 'lucide-react';
import Layout from '../components/Layout';
import LitigeFormSheet from '../components/LitigeFormSheet';
import { LitigeGauge, RetourKpis, RiskFactors, RetourCard, AddRetourCard } from '../components/retours/RetoursDashboard';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent } from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════
// Catalogue de motifs (aligné avec le backend)
// ═══════════════════════════════════════════════════════════════════
const MOTIFS = [
  {
    code: 'panneaux_insuffisants',
    label: 'Nombre de panneaux insuffisant',
    precisions: ['Moins que souhaité', 'Trop peu de villes couvertes', 'Volume trop faible', 'Autre'],
  },
  {
    code: 'emplacement_pas_interessant',
    label: 'Emplacement pas intéressant',
    precisions: ['Mal visible', 'Zone peu passante', 'Concurrence trop proche', 'Autre'],
  },
  {
    code: 'cout_eleve',
    label: 'Coût élevé',
    precisions: ['Tarif mensuel trop haut', 'Frais de conception élevés', 'Concurrent moins cher', 'Autre'],
  },
  {
    code: 'duree_engagement',
    label: "Durée d'engagement",
    precisions: ['Souhaite plus court', 'Souhaite plus long', 'Refuse l\'engagement', 'Autre'],
  },
  {
    code: 'budget_gele',
    label: 'Budget gelé ou report',
    precisions: ['Budget non voté', 'Report trimestre suivant', 'Report saison prochaine', 'Autre'],
  },
  {
    code: 'autre',
    label: 'Autre',
    precisions: ['À préciser'],
  },
];

const MOTIF_BADGE_COLOR = {
  cout_eleve:                    'bg-amber-100 text-amber-800 border-amber-300',
  panneaux_insuffisants:         'bg-orange-100 text-orange-800 border-orange-300',
  emplacement_pas_interessant:   'bg-rose-100 text-rose-800 border-rose-300',
  duree_engagement:              'bg-purple-100 text-purple-800 border-purple-300',
  budget_gele:                   'bg-slate-200 text-slate-800 border-slate-400',
  autre:                         'bg-slate-100 text-slate-700 border-slate-300',
};

const EMPTY_EXTRA = { contact: '', telephone: '', email: '', secteur: '', zone: '', canal: '', panneaux_demandes: '', panneaux_proposes: '', budget_propose: '', priorite: 'moyenne', decision: '' };

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso); const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return `il y a ${Math.floor(diff / 86400)} jour${Math.floor(diff / 86400) > 1 ? 's' : ''}`;
  if (diff < 86400 * 30) return `il y a ${Math.floor(diff / (86400 * 7))} semaine${Math.floor(diff / (86400 * 7)) > 1 ? 's' : ''}`;
  return `il y a ${Math.floor(diff / (86400 * 30))} mois`;
};


// ═══════════════════════════════════════════════════════════════════
// Panneau latéral : Faire un retour (création + édition)
// ═══════════════════════════════════════════════════════════════════
function RetourFormPanel({ open, onClose, initialData, prospects, onSaved }) {
  const [type, setType] = useState('Prospect');
  const [clientName, setClientName] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [motifs, setMotifs] = useState({});
  const [commentaire, setCommentaire] = useState('');
  const [extra, setExtra] = useState(EMPTY_EXTRA);
  const [saving, setSaving] = useState(false);
  const setX = (k, v) => setExtra((e) => ({ ...e, [k]: v }));

  useEffect(() => {
    if (open) {
      if (initialData) {
        setType(initialData.type || 'Prospect');
        setClientName(initialData.client_name || '');
        const mm = {};
        (initialData.motifs || []).forEach(m => { mm[m.motif] = { checked: true, precision: m.precision || '' }; });
        setMotifs(mm);
        setCommentaire(initialData.commentaire || '');
        setExtra(Object.fromEntries(Object.keys(EMPTY_EXTRA).map((k) => [k, initialData[k] ?? EMPTY_EXTRA[k]])));
      } else {
        setExtra(EMPTY_EXTRA);
        setType('Prospect');
        setClientName('');
        setClientSearch('');
        setMotifs({});
        setCommentaire('');
      }
    }
  }, [open, initialData]);

  const suggestions = useMemo(() => {
    const s = (clientSearch || '').toLowerCase();
    return (prospects || []).filter(p => (p.entreprise || p.nom || '').toLowerCase().includes(s)).slice(0, 6);
  }, [clientSearch, prospects]);

  const toggleMotif = (code) => {
    setMotifs(prev => ({ ...prev, [code]: prev[code]?.checked ? { checked: false, precision: '' } : { checked: true, precision: prev[code]?.precision || '' } }));
  };

  const setPrecision = (code, val) => {
    setMotifs(prev => ({ ...prev, [code]: { checked: true, precision: val } }));
  };

  const save = async () => {
    if (!clientName.trim()) { toast.error('Veuillez choisir un client'); return; }
    const activeMotifs = Object.entries(motifs).filter(([, v]) => v.checked).map(([code, v]) => ({ motif: code, precision: v.precision || '' }));
    if (activeMotifs.length === 0) { toast.error('Sélectionnez au moins un motif'); return; }
    setSaving(true);
    try {
      const payload = {
        client_name: clientName.trim(), type, motifs: activeMotifs, commentaire, ...extra,
        panneaux_demandes: parseInt(extra.panneaux_demandes, 10) || 0,
        panneaux_proposes: parseInt(extra.panneaux_proposes, 10) || 0,
        budget_propose: parseFloat(extra.budget_propose) || 0,
      };
      let r;
      if (initialData?.id) {
        r = await axios.put(`${API}/api/retours-clients/${initialData.id}`, payload);
      } else {
        r = await axios.post(`${API}/api/retours-clients`, payload);
      }
      toast.success(initialData?.id ? 'Retour mis à jour' : 'Retour enregistré');
      onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="retour-form-panel">
      <div className="flex-1 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{initialData?.id ? 'Modifier le retour' : 'Faire un retour'}</h2>
            <p className="text-sm text-slate-500">Pourquoi le contrat n'a pas été signé</p>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 p-6 space-y-5">
          {/* Client */}
          <div>
            <Label className="text-sm font-bold text-slate-800">Client</Label>
            <div className="relative mt-1">
              <Input
                value={clientName}
                onChange={(e) => { setClientName(e.target.value); setClientSearch(e.target.value); }}
                placeholder="Rechercher ou saisir un nom d'entreprise…"
                data-testid="retour-input-client"
              />
              {clientSearch && suggestions.length > 0 && clientName !== suggestions[0]?.entreprise && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                  {suggestions.map((p) => (
                    <button
                      key={p.id || p.entreprise}
                      onClick={() => {
                        setClientName(p.entreprise || p.nom || ''); setClientSearch('');
                        setExtra((e) => ({ ...e, contact: e.contact || p.nom || p.contact || '', telephone: e.telephone || p.telephone || '', email: e.email || p.email || '', secteur: e.secteur || p.secteur || '' }));
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
                    >
                      {p.entreprise || p.nom}
                      <span className="text-xs text-slate-400 ml-2">{p.contact || ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Type */}
          <div>
            <Label className="text-sm font-bold text-slate-800">Type</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {['Prospect', 'Client'].map((t) => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  className={`px-4 py-2.5 rounded-lg font-semibold text-sm border-2 transition ${type === t ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'}`}
                  data-testid={`retour-type-${t}`}
                >
                  {t === 'Client' ? 'Client existant' : t}
                </button>
              ))}
            </div>
          </div>

          {/* Détails du retour */}
          <div className="grid grid-cols-2 gap-3">
            {[
              ['contact', 'Contact', 'text'], ['telephone', 'Téléphone', 'tel'],
              ['secteur', 'Secteur', 'text'], ['zone', 'Ville / zone', 'text'],
              ['panneaux_demandes', 'Panneaux demandés', 'number'], ['panneaux_proposes', 'Panneaux proposés', 'number'],
              ['budget_propose', 'Budget proposé (FCFA)', 'number'],
            ].map(([k, l, t]) => (
              <div key={k}>
                <Label className="text-xs font-bold text-slate-700">{l}</Label>
                <Input type={t} value={extra[k]} onChange={(e) => setX(k, e.target.value)} className="mt-1" data-testid={`retour-input-${k}`} />
              </div>
            ))}
            <div>
              <Label className="text-xs font-bold text-slate-700">Canal</Label>
              <select value={extra.canal} onChange={(e) => setX('canal', e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-2 text-sm bg-white" data-testid="retour-input-canal">
                <option value="">Choisir…</option>
                {['Visite terrain', 'Téléphone', 'E-mail', 'WhatsApp', 'Réunion'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-bold text-slate-700">Priorité</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[['basse', 'Basse'], ['moyenne', 'Moyenne'], ['haute', 'Haute']].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setX('priorite', k)} className={`h-9 rounded-lg text-sm font-semibold border-2 transition-colors ${extra.priorite === k ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-700'}`} data-testid={`retour-priorite-${k}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-bold text-slate-700">Décision finale</Label>
              <select value={extra.decision} onChange={(e) => setX('decision', e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-2 text-sm bg-white" data-testid="retour-input-decision">
                <option value="">En cours</option>
                <option value="signe">Contrat finalement signé</option>
                <option value="perdu">Définitivement perdu</option>
              </select>
            </div>
          </div>

          {/* Motifs de refus */}
          <div>
            <Label className="text-sm font-bold text-slate-800">Motifs de refus</Label>
            <div className="mt-2 rounded-xl border border-slate-200 divide-y divide-slate-100">
              <div className="grid grid-cols-[1fr_auto] gap-3 px-4 py-2 bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wide">
                <span>Motif</span>
                <span className="pr-1">Précision</span>
              </div>
              {MOTIFS.map((m) => {
                const st = motifs[m.code] || { checked: false, precision: '' };
                return (
                  <div key={m.code} className={`grid grid-cols-[1fr_170px] gap-3 items-center px-4 py-2.5 ${st.checked ? 'bg-blue-50/40' : ''}`}>
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={!!st.checked}
                        onChange={() => toggleMotif(m.code)}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        data-testid={`motif-check-${m.code}`}
                      />
                      <span className="font-medium text-slate-800">{m.label}</span>
                    </label>
                    <select
                      value={st.precision}
                      onChange={(e) => setPrecision(m.code, e.target.value)}
                      disabled={!st.checked}
                      className={`text-sm border rounded-md px-2 py-1.5 ${st.checked ? 'border-blue-300 bg-white' : 'border-slate-200 bg-slate-50 text-slate-400'}`}
                      data-testid={`motif-precision-${m.code}`}
                    >
                      <option value="">Choisir…</option>
                      {m.precisions.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Commentaire */}
          <div>
            <Label className="text-sm font-bold text-slate-800">Commentaire</Label>
            <Textarea
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              rows={4}
              placeholder="Ce que le client a dit, ce qui pourrait le faire changer d'avis…"
              className="mt-1"
              data-testid="retour-commentaire"
            />
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={save}
            disabled={saving}
            data-testid="retour-save-btn"
          >
            {saving ? 'Enregistrement…' : (initialData?.id ? 'Mettre à jour' : 'Enregistrer le retour')}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Modal détails d'un retour
// ═══════════════════════════════════════════════════════════════════
function RetourDetailModal({ retour, onClose, onEdit, onRelancer }) {
  if (!retour) return null;
  const isProspect = retour.type === 'Prospect';
  return (
    <Dialog open={!!retour} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl p-0" data-testid="retour-detail-modal">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-0">
          {/* Colonne principale */}
          <div className="p-6 space-y-5 overflow-y-auto max-h-[85vh]">
            <div>
              <div className="text-xs text-slate-500">Retours clients / {retour.client_name}</div>
              <div className="flex items-center gap-3 mt-1">
                <h2 className="text-3xl font-extrabold text-slate-900">{retour.client_name}</h2>
                <Badge className={isProspect ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-slate-200 text-slate-700 border-slate-300'}>{retour.type}</Badge>
              </div>
              <div className="text-sm text-slate-500 mt-1">Retour saisi par {retour.commercial_name || retour.created_by || '—'} · {timeAgo(retour.created_at)}</div>
            </div>

            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-bold text-slate-800 mb-3">Motifs de refus</h3>
                <div className="space-y-2">
                  {(retour.motifs || []).map((m, idx) => (
                    <div key={idx} className={`grid grid-cols-[1fr_auto] items-center px-4 py-2.5 rounded-lg border ${MOTIF_BADGE_COLOR[m.motif] || 'bg-slate-50 border-slate-200'}`}>
                      <div className="font-semibold text-sm">{MOTIFS.find(x => x.code === m.motif)?.label || m.motif}</div>
                      <div className="text-sm bg-white rounded-md border border-slate-200 px-3 py-1 text-slate-700">{m.precision || '—'}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-bold text-slate-800 mb-2">Commentaire</h3>
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{retour.commentaire || 'Aucun commentaire.'}</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-bold text-slate-800 mb-3">Historique</h3>
                <div className="space-y-3">
                  {(retour.historique || []).map((h, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="mt-1">
                        {idx === 0 ? <CheckCircle2 className="h-4 w-4 text-blue-600" /> : <Circle className="h-4 w-4 text-slate-300" />}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-slate-800">{h.titre}</div>
                        <div className="text-xs text-slate-500">{h.acteur} · {timeAgo(h.date)}</div>
                      </div>
                    </div>
                  ))}
                  {(retour.historique || []).length === 0 && (
                    <div className="text-sm text-slate-400 italic">Pas d'historique.</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Colonne latérale : Informations + Actions */}
          <div className="p-6 space-y-4 bg-slate-50 border-l border-slate-200">
            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-bold text-slate-800 mb-3">Informations</h3>
                <div className="space-y-2 text-sm">
                  {[
                    ['Contact', retour.contact],
                    ['Téléphone', retour.telephone],
                    ['Email', retour.email],
                    ['Secteur', retour.secteur],
                    ['Zone', retour.zone],
                    ['Commercial', retour.commercial_name || retour.created_by],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between border-b border-slate-100 py-1.5 last:border-0">
                      <span className="text-slate-500">{k}</span>
                      <span className="font-semibold text-slate-800 text-right">{v || '—'}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={() => onRelancer(retour)} data-testid="retour-relancer-btn">
              <ArrowRight className="h-4 w-4" />
              {isProspect ? 'Relancer le prospect' : 'Relancer le client'}
            </Button>
            <Button variant="outline" className="w-full gap-2" onClick={() => onEdit(retour)} data-testid="retour-edit-btn">
              <Edit3 className="h-4 w-4" /> Modifier le retour
            </Button>
            <Button variant="outline" className="w-full" onClick={() => onRelancer(retour, 'suivi')}>
              Créer une action de suivi
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Page principale
// ═══════════════════════════════════════════════════════════════════
const motifLabel = (code) => MOTIFS.find((x) => x.code === code)?.label || code;

export default function RetoursClients({ user, onLogout }) {
  const [retours, setRetours] = useState([]);
  const [stats, setStats] = useState({ retours_ce_mois: 0, prospects_perdus: 0, motif_principal: '—', panneaux_insuffisants: 0 });
  const [litigeStats, setLitigeStats] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editRetour, setEditRetour] = useState(null);
  const [detailRetour, setDetailRetour] = useState(null);
  const [litigeInitial, setLitigeInitial] = useState(null);
  const [showLitige, setShowLitige] = useState(false);
  const [contrats, setContrats] = useState([]);

  const loadAll = async () => {
    const [r, st, ls] = await Promise.all([
      axios.get(`${API}/api/retours-clients`).catch(() => ({ data: [] })),
      axios.get(`${API}/api/retours-clients/stats`).catch(() => ({ data: null })),
      axios.get(`${API}/api/retours-clients/litige-stats`).catch(() => ({ data: null })),
    ]);
    setRetours(r.data || []);
    if (st.data) setStats(st.data);
    setLitigeStats(ls.data);
  };

  useEffect(() => {
    loadAll();
    axios.get(`${API}/api/prospects`).then((r) => setProspects(r.data || [])).catch(() => {});
    axios.get(`${API}/api/contrats`).then((r) => setContrats(r.data || [])).catch(() => {});
  }, []);

  const monthStart = useMemo(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1).toISOString(); }, []);
  const FILTERS = [
    ['all', 'Tous', () => true],
    ['Prospect', 'Prospects', (r) => r.type === 'Prospect'],
    ['Client', 'Clients', (r) => r.type === 'Client'],
    ['ce_mois', 'Ce mois', (r) => (r.created_at || '') >= monthStart],
    ['risque', 'Risque élevé', (r) => (r.risk_score || 0) >= 60],
  ];
  const activeFn = FILTERS.find((f) => f[0] === filter)[2];
  const visible = retours.filter((r) => activeFn(r) && (!search || (r.client_name || '').toLowerCase().includes(search.toLowerCase())));

  const relancer = async (r) => {
    try {
      await axios.post(`${API}/api/retours-clients/${r.id}/action`, { titre: 'Relance' });
      toast.success(`Relance enregistrée pour ${r.client_name}`);
      loadAll();
    } catch { toast.error('Erreur lors de la relance'); }
  };
  const appeler = (r) => {
    if (!r.telephone) { toast.error("Aucun numéro de téléphone pour ce client"); return; }
    window.location.href = `tel:${r.telephone.replace(/\s/g, '')}`;
  };
  const openLitige = (r) => {
    setLitigeInitial(r ? { retour_id: r.id, entreprise: r.client_name, contact_nom: r.contact || '', contact_telephone: r.telephone || '', contact_email: r.email || '' } : null);
    setShowLitige(true);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-2 md:p-4 space-y-5" data-testid="retours-clients-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-4xl font-extrabold text-[#0f1a3a] tracking-tight">Retours clients</h1>
            <p className="text-sm text-slate-500 mt-1">Pourquoi les contrats n'ont pas été signés, vu du terrain.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => openLitige(null)} className="bg-red-500 hover:bg-red-600 text-white gap-2 h-11 px-5 rounded-xl font-bold" data-testid="btn-faire-litige">
              <Gavel className="h-4 w-4" /> Litige
            </Button>
            <Button className="bg-blue-700 hover:bg-blue-800 text-white gap-2 h-11 px-5 rounded-xl font-bold" onClick={() => { setEditRetour(null); setShowForm(true); }} data-testid="btn-faire-retour">
              <Plus className="h-4 w-4" /> Faire un retour
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-4">
          <LitigeGauge data={litigeStats} />
          <div className="space-y-4">
            <RetourKpis stats={stats} />
            <RiskFactors facteurs={litigeStats?.facteurs} />
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {FILTERS.map(([code, label, fn]) => (
              <button
                key={code}
                onClick={() => setFilter(code)}
                className={`px-5 h-10 rounded-full text-sm font-bold transition-colors ${filter === code ? 'bg-blue-700 text-white' : 'bg-white text-slate-600 hover:text-slate-900'}`}
                data-testid={`filter-${code}`}
              >
                {label} · {retours.filter(fn).length}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-4 top-3 h-4 w-4 text-slate-400" />
            <input
              className="h-10 w-72 rounded-full bg-white pl-10 pr-4 text-sm outline-none focus:ring-2 focus:ring-blue-200"
              placeholder="Rechercher un client…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="input-search-client"
            />
          </div>
        </div>

        {visible.length === 0 ? (
          <div className="text-center py-10 text-slate-400" data-testid="retours-empty">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-40" />
            Aucun retour {filter !== 'all' || search ? 'pour ce filtre' : 'enregistré'}
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5" data-testid="retours-grid">
            {visible.map((r, i) => (
              <RetourCard
                key={r.id}
                r={r}
                delay={i * 60}
                motifLabel={motifLabel}
                timeAgo={timeAgo}
                onCall={() => appeler(r)}
                onRelance={() => relancer(r)}
                onOpen={() => setDetailRetour(r)}
                onLitige={() => openLitige(r)}
              />
            ))}
          </div>
        )}

        <AddRetourCard onClick={() => { setEditRetour(null); setShowForm(true); }} />
      </div>

      <RetourFormPanel
        open={showForm}
        onClose={() => setShowForm(false)}
        initialData={editRetour}
        prospects={prospects}
        onSaved={loadAll}
      />
      <RetourDetailModal
        retour={detailRetour}
        onClose={() => setDetailRetour(null)}
        onEdit={(r) => { setDetailRetour(null); setEditRetour(r); setShowForm(true); }}
        onRelancer={(r) => { setDetailRetour(null); relancer(r); }}
      />
      <LitigeFormSheet
        open={showLitige}
        onClose={() => setShowLitige(false)}
        contrats={contrats}
        initial={litigeInitial}
        onSaved={() => { setShowLitige(false); loadAll(); }}
      />
    </Layout>
  );
}
